import { Static, Type } from '@sinclair/typebox'
import { BaseModelSchema, Nullable } from '../../common/base-model'

export enum SwaggerDefinitionStatus {
    /** Imported but not exposed yet: the assigned tenant does not see it. */
    DRAFT = 'DRAFT',
    /** Exposed to the assigned tenant's flow builder. */
    ENABLED = 'ENABLED',
    /** Temporarily withdrawn without losing the route selection. */
    DISABLED = 'DISABLED',
}

export enum SwaggerSourceType {
    /** Fetched from a URL, refreshable. */
    URL = 'URL',
    /** Pasted or uploaded document, not refreshable. */
    RAW = 'RAW',
}

export enum SwaggerAudience {
    /**
     * Visible to the tenants listed in `tenantIds`, and to nobody else. An
     * empty list is a first-class choice rather than an empty state: an
     * unassigned Swagger is visible to nobody, which is the safe default while
     * a definition is still being prepared.
     */
    CLIENTS = 'CLIENTS',
    /**
     * Visible to every tenant, including the ones created after the assignment.
     *
     * Deliberately *not* modelled as "every tenant id enumerated in
     * `tenantIds`". That would leave out tenants created later, write one join
     * row per tenant per definition, and — since slug uniqueness is scoped to
     * the assigned tenants — make the slug of a shared Swagger collide with
     * every per-tenant slug at once.
     */
    ALL = 'ALL',
}

/**
 * One selectable route of a definition. Action names are minted once, at
 * import, and never recomputed — a flow stores the action name, so recomputing
 * it after an upstream spec change would silently repoint the step at another
 * endpoint.
 */
export const SwaggerOperation = Type.Object({
    actionName: Type.String(),
    method: Type.String(),
    path: Type.String(),
    displayName: Type.String(),
    description: Type.String(),
    tags: Type.Array(Type.String()),
    deprecated: Type.Boolean(),
    /** Whether the route is exposed to the tenant. */
    enabled: Type.Boolean(),
    /** Cleared when the route disappears from the upstream spec on refresh. */
    present: Type.Boolean(),
})

export type SwaggerOperation = Static<typeof SwaggerOperation>

/**
 * A Swagger imported by a Kizeo administrator and assigned to zero or more
 * tenants. The OpenAPI document itself is not part of this model: it stays
 * server-side and is only used to build the piece metadata.
 */
export const SwaggerDefinition = Type.Object({
    ...BaseModelSchema,
    /** Who the definition is exposed to. */
    audience: Type.Enum(SwaggerAudience),
    /**
     * Owning tenants when `audience` is `CLIENTS`. Empty means unassigned,
     * therefore visible to nobody. Always empty when `audience` is `ALL` —
     * the two are mutually exclusive so there is only ever one source of truth
     * for who can see the Swagger.
     */
    tenantIds: Type.Array(Type.String()),
    /**
     * Display names of `tenantIds`, in the same order. Carried alongside the
     * ids so the admin form can label an assignment without issuing one lookup
     * per assigned client.
     */
    tenantNames: Type.Array(Type.String()),
    /**
     * Kizeo customer numbers of `tenantIds`, in the same order — the `Client
     * ID` the admin interface identifies a client by, and the only one of the
     * three that an administrator reads outside Khub. `null` for a tenant that
     * has none.
     */
    tenantCustomerIds: Type.Array(Nullable(Type.String())),
    /** Trailing part of the piece name (`swagger_<slug>`). Immutable. */
    slug: Type.String(),
    pieceName: Type.String(),
    displayName: Type.String(),
    description: Type.String(),
    logoUrl: Nullable(Type.String()),
    version: Type.String(),
    baseUrl: Type.String(),
    status: Type.Enum(SwaggerDefinitionStatus),
    sourceType: Type.Enum(SwaggerSourceType),
    sourceUrl: Nullable(Type.String()),
    /** Spec version as submitted, before conversion (`2.0`, `3.0.3`, …). */
    originalSpecVersion: Type.String(),
    operations: Type.Array(SwaggerOperation),
    lastImportedAt: Type.String(),
})

export type SwaggerDefinition = Static<typeof SwaggerDefinition>

/** Row shape of the admin list: counts instead of the full route list. */
export const SwaggerDefinitionSummary = Type.Object({
    ...BaseModelSchema,
    audience: Type.Enum(SwaggerAudience),
    tenantIds: Type.Array(Type.String()),
    tenantNames: Type.Array(Type.String()),
    tenantCustomerIds: Type.Array(Nullable(Type.String())),
    slug: Type.String(),
    pieceName: Type.String(),
    displayName: Type.String(),
    logoUrl: Nullable(Type.String()),
    version: Type.String(),
    baseUrl: Type.String(),
    status: Type.Enum(SwaggerDefinitionStatus),
    sourceType: Type.Enum(SwaggerSourceType),
    sourceUrl: Nullable(Type.String()),
    originalSpecVersion: Type.String(),
    operationsCount: Type.Number(),
    enabledOperationsCount: Type.Number(),
    lastImportedAt: Type.String(),
})

export type SwaggerDefinitionSummary = Static<typeof SwaggerDefinitionSummary>

/**
 * What a tenant is allowed to know about the swaggers assigned to it: enough
 * to render the piece in the step selector, nothing more. The document itself
 * never reaches the browser.
 */
export const SwaggerPieceRef = Type.Object({
    pieceName: Type.String(),
    displayName: Type.String(),
    description: Type.String(),
    logoUrl: Nullable(Type.String()),
    version: Type.String(),
})

export type SwaggerPieceRef = Static<typeof SwaggerPieceRef>

export const CreateSwaggerDefinitionRequest = Type.Object({
    /** Source URL of the document. Mutually exclusive with `spec`. */
    sourceUrl: Type.Optional(Type.String({ minLength: 1 })),
    /** Inline Swagger 2 or OpenAPI 3 document, JSON or YAML. */
    spec: Type.Optional(Type.Unknown()),
    /** Defaults to `CLIENTS`. `ALL` ignores `tenantIds`. */
    audience: Type.Optional(Type.Enum(SwaggerAudience)),
    /** Clients to assign on import. Empty (or omitted) leaves it unassigned. */
    tenantIds: Type.Optional(Type.Array(Type.String())),
    displayName: Type.Optional(Type.String({ minLength: 1 })),
    logoUrl: Type.Optional(Type.String()),
    /**
     * Overrides the base URL the piece builder derives from the document's
     * `servers` entry. Needed when the document declares none — common on
     * specs auto-generated from a Postman collection — which would otherwise
     * leave every action pointing at a host-less path.
     */
    baseUrl: Type.Optional(Type.String({ minLength: 1 })),
})

export type CreateSwaggerDefinitionRequest = Static<
    typeof CreateSwaggerDefinitionRequest
>

export const UpdateSwaggerDefinitionRequest = Type.Object({
    /**
     * Switching to `ALL` clears `tenantIds`: the two ways of describing who
     * sees the Swagger are mutually exclusive, and keeping a stale list around
     * would leave two sources of truth. Switching back therefore starts from an
     * empty selection.
     */
    audience: Type.Optional(Type.Enum(SwaggerAudience)),
    /**
     * Exhaustive list of the clients this Swagger is assigned to. Absent
     * leaves the assignment alone; an empty array unassigns it from everyone.
     * Ignored when `audience` is `ALL`.
     */
    tenantIds: Type.Optional(Type.Array(Type.String())),
    displayName: Type.Optional(Type.String({ minLength: 1 })),
    description: Type.Optional(Type.String()),
    logoUrl: Type.Optional(Nullable(Type.String())),
    status: Type.Optional(Type.Enum(SwaggerDefinitionStatus)),
    /**
     * Corrects the base URL after import — e.g. when the document had no
     * `servers` entry to derive it from. A `refresh` never overwrites a base
     * URL that is already set, so this is the only way to fix or change it.
     */
    baseUrl: Type.Optional(Type.String({ minLength: 1 })),
    /**
     * Exhaustive list of the routes to expose. Action names absent from the
     * list are disabled; unknown names are rejected.
     */
    enabledActionNames: Type.Optional(Type.Array(Type.String())),
})

export type UpdateSwaggerDefinitionRequest = Static<
    typeof UpdateSwaggerDefinitionRequest
>

export const ListSwaggerDefinitionsRequest = Type.Object({
    cursor: Type.Optional(Type.String()),
    limit: Type.Optional(Type.Number({ default: 20 })),
    tenantId: Type.Optional(Type.String()),
    status: Type.Optional(Type.Array(Type.Enum(SwaggerDefinitionStatus))),
    search: Type.Optional(Type.String()),
})

export type ListSwaggerDefinitionsRequest = Static<
    typeof ListSwaggerDefinitionsRequest
>

export const SwaggerDefinitionIdParams = Type.Object({
    id: Type.String(),
})

export type SwaggerDefinitionIdParams = Static<
    typeof SwaggerDefinitionIdParams
>
